import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { Link, Text } from "../";

import styles from "./Breadcrumbs.module.scss";

/**
 * Props for the BreadcrumbsLink component
 * @extends ComponentPropsWithoutRef<"a">
 * @extends LayoutUtilProps
 */
export type BreadcrumbsLinkProps = ComponentPropsWithoutRef<"a"> &
  LayoutUtilProps;

/**
 * BreadcrumbsLink component for individual breadcrumb navigation items.
 *
 * Features:
 * - Renders as a clickable link with secondary appearance
 * - Supports all standard anchor element props
 * - Includes layout utilities for positioning and spacing
 * - Uses small text size for consistent styling
 * - Fully accessible with proper focus management
 * - Automatically applies secondary link styling
 *
 * @example
 * <BreadcrumbsLink href="/dashboard">Dashboard</BreadcrumbsLink>
 */
export const BreadcrumbsLink = forwardRef<
  HTMLAnchorElement,
  BreadcrumbsLinkProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const { children, className, style, ...rest } = componentProps;

  const classes = cx([styles["breadcrumbs-link"]], className, {});

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <Link
      tabIndex={0}
      data-anv="breadcrumbs-link"
      style={styleCombined}
      appearance="secondary"
      className={classes}
      {...rest}
      ref={ref}
    >
      <Text size="small" inline>
        {children}
      </Text>
    </Link>
  );
});

BreadcrumbsLink.displayName = "BreadcrumbsLink";
